import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import {
  ArrowLeft,
  Shield,
  Send,
  Search,
  AlertCircle,
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import backgroundVerificationService from '../../../api/services/backgroundVerificationService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import { API_BASE_URL } from '../../../api/config';

const InitiateBackgroundVerification = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { user } = useAuth();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  // const [isSubmitting, setIsSubmitting] = useState(false); // Using mutation.isPending instead
  const [initialEmployeeId, setInitialEmployeeId] = useState(null);
  const [formData, setFormData] = useState({
    candidateId: '',
    employeeId: '', // Changed from vendor to employee
    priority: 'normal',
    expectedCompletionDate: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [debouncedCandidateSearch, setDebouncedCandidateSearch] = useState('');
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [selectedCandidateData, setSelectedCandidateData] = useState(null);
  const candidateDropdownRef = useRef(null);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const employeeDropdownRef = useRef(null);

  // Load verification data if in edit mode
  const { data: verificationResult, isLoading: loadingVerification } = useQuery({
    queryKey: ['backgroundVerification', id],
    queryFn: () => backgroundVerificationService.getBackgroundVerificationById(id),
    enabled: isEditMode,
  });

  // Populate form if in edit mode
  useEffect(() => {
    if (isEditMode && verificationResult?.data) {
      const verification = verificationResult.data;

      const vendorReferenceId = verification.vendor?.id || verification.vendor?._id || verification.vendorId?._id || verification.vendorId || '';
      const vendorDisplayId = verification.vendor?.employeeId || verification.vendor?.vendorId || '';

      // Store initial employee ID for comparison
      if (initialEmployeeId === null && vendorReferenceId) {
        setInitialEmployeeId(vendorReferenceId);
      }

      setFormData({
        candidateId: verification.candidateId?._id || verification.candidateId,
        employeeId: vendorReferenceId,
        priority: verification.priority || 'normal',
        expectedCompletionDate: verification.expectedCompletionDate ? new Date(verification.expectedCompletionDate).toISOString().split('T')[0] : '',
        notes: verification.notes || ''
      });

      // Special handling for selected candidate and employee to show tags/info
      if (verification.candidateId) {
        const candidateInfo = verification.candidateInfo || {};
        const candidateName = candidateInfo.name || verification.candidateName || '';
        const candidateEmail = candidateInfo.email || verification.candidateEmail || 'N/A';

        // Construct display candidate from verification response to ensure it's accurate
        const displayCandidate = {
          ...(typeof verification.candidateId === 'object' ? verification.candidateId : {}),
          _id: verification.candidateId?._id || verification.candidateId,
          id: verification.candidateId?.id || verification.candidateId,
          firstName: candidateName,
          lastName: '',
          email: candidateEmail,
          department: candidateInfo.department || (typeof verification.candidateId === 'object' ? (verification.candidateId.department || verification.candidateId.departmentId?.name) : ''),
          position: candidateInfo.position || (typeof verification.candidateId === 'object' ? (verification.candidateId.position || verification.candidateId.jobTitle) : ''),
          isFromVerification: true // Flag to know we're using verification-level data
        };
        setSelectedCandidateData(displayCandidate);
        setCandidateSearchTerm(candidateName);
      }

      if (verification.vendor) {
        const vendorReferenceId = verification.vendor?.id || verification.vendor?._id || verification.vendorId?._id || verification.vendorId || '';
        const vendorDisplayId = verification.vendor?.employeeId || verification.vendor?.vendorId || '';
        
        // Map vendor to a structure the UI expects for "selectedEmployeeData"
        const mappedEmployee = {
          _id: vendorReferenceId,
          employeeId: vendorDisplayId,
          personalInfo: {
            firstName: verification.vendor.name?.split(' ')[0] || '',
            lastName: verification.vendor.name?.split(' ').slice(1).join(' ') || ''
          },
          contactInfo: {
            personalEmail: verification.vendor.email,
            phone: verification.vendor.phone
          },
          name: verification.vendor.name
        };
        setSelectedEmployeeData(mappedEmployee);
        setEmployeeSearchTerm(verification.vendor.name || '');
      }
    }
  }, [isEditMode, verificationResult, initialEmployeeId]);

  // Load candidates with verified documents using React Query
  const { data: eligibleCandidatesResult, isLoading: loadingCandidates, error: candidatesError } = useQuery({
    queryKey: ['eligibleCandidatesForBackgroundVerification'],
    queryFn: async () => {

      const result = await backgroundVerificationService.getEligibleCandidates();


      if (result && result.responseCode === 200 && result.data) {
        return result.data.candidates || [];
      }
      return [];
    },
    staleTime: 0,
    retry: 2,
  });

  // Load existing background verifications to filter out already processed candidates
  const { data: existingVerifications = [], isLoading: loadingVerifications } = useQuery({
    queryKey: ['existingBackgroundVerifications'],
    queryFn: async () => {

      const result = await backgroundVerificationService.getBackgroundVerifications({ limit: 1000 });


      if (result?.responseCode === 200 && result.data) {
        if (Array.isArray(result.data.verifications)) return result.data.verifications;
        if (Array.isArray(result.data.backgroundVerifications)) return result.data.backgroundVerifications;
        if (Array.isArray(result.data)) return result.data;
      }

      if (Array.isArray(result?.verifications)) return result.verifications;
      if (Array.isArray(result?.backgroundVerifications)) return result.backgroundVerifications;
      if (Array.isArray(result)) return result;

      return [];
    },
    staleTime: 0,
    retry: 2,
  });

  // Filter out candidates who already have background verifications
  const existingVerificationCandidateIds = useMemo(() => {
    const candidateIds = new Set();
    (existingVerifications || []).forEach((verification) => {
      const candidateId = verification?.candidateId || verification?.candidateId?._id || verification?.candidateId?.id;
      if (candidateId) {
        candidateIds.add(candidateId.toString());
      }
    });
    return candidateIds;
  }, [existingVerifications]);

  const availableCandidates = useMemo(() => {
    if (!eligibleCandidatesResult) {
      // If we are in edit mode and have the selected candidate data, return at least that
      return isEditMode && selectedCandidateData ? [selectedCandidateData] : [];
    }

    let candidates = (eligibleCandidatesResult || []).filter((candidate) => {
      const candidateId = candidate.id || candidate._id;
      return !existingVerificationCandidateIds.has(candidateId?.toString());
    });

    // If in edit mode, make sure the currently selected candidate is in the list
    if (isEditMode && selectedCandidateData) {
      const selectedId = (selectedCandidateData.id || selectedCandidateData._id)?.toString();
      const isAlreadyIncluded = candidates.some(c => (c.id || c._id)?.toString() === selectedId);
      if (!isAlreadyIncluded) {
        candidates = [selectedCandidateData, ...candidates];
      }
    }

    return candidates;
  }, [eligibleCandidatesResult, existingVerificationCandidateIds, isEditMode, selectedCandidateData]);

  const hasCandidates = availableCandidates.length > 0;
  const loading = loadingCandidates;

  // Debounce candidate search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCandidateSearch(candidateSearchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [candidateSearchTerm]);

  // Debounce employee search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // React Query: Fetch default employees (20 by default when dropdown opens)
  const { data: defaultEmployeesData, isLoading: isLoadingDefaultEmployees } = useQuery({
    queryKey: ['background-verification-default-employees'],
    queryFn: async () => {
      const baseUrl = API_BASE_URL;
      const url = `${baseUrl}/crm/field-sales/sales-reps?role=hr_manager,hr_executive,employee&limit=20`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.salesReps) {
          return data.data.salesReps || [];
        }
      }
      return [];
    },
    enabled: showEmployeeDropdown && (!debouncedEmployeeSearch || debouncedEmployeeSearch.trim().length === 0),
    staleTime: 0,
  });

  // React Query: Search Employees (only fetch on search)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['background-verification-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      if (!debouncedEmployeeSearch || !debouncedEmployeeSearch.trim()) {
        return [];
      }

      const baseUrl = API_BASE_URL;
      const url = `${baseUrl}/crm/field-sales/sales-reps?role=hr_manager,hr_executive,employee&search=${encodeURIComponent(debouncedEmployeeSearch.trim())}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.salesReps) {
          return data.data.salesReps || [];
        }
      }
      return [];
    },
    enabled: !!debouncedEmployeeSearch && debouncedEmployeeSearch.trim().length > 0,
    staleTime: 0,
  });

  // Get employees to display in dropdown (default employees when no search, searched results when searching)
  const dropdownEmployees = (debouncedEmployeeSearch && debouncedEmployeeSearch.trim().length > 0)
    ? (searchedEmployeesData || [])
    : (defaultEmployeesData || []);

  // Close employee dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        if (showEmployeeDropdown && employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
          setShowEmployeeDropdown(false);
          setTouched(prev => ({ ...prev, employeeId: true }));
          const error = validateFieldInternal('employeeId', formData.employeeId);
          setErrors(prev => ({ ...prev, employeeId: error }));
        }
      } catch (_) { }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showEmployeeDropdown]);

  // Filter candidates based on search term
  const filteredCandidates = useMemo(() => {
    if (!candidateSearchTerm || !candidateSearchTerm.trim()) {
      return availableCandidates;
    }
    const searchLower = candidateSearchTerm.toLowerCase().trim();
    return availableCandidates.filter((candidate) => {
      const name = `${candidate.firstName || ''} ${candidate.lastName || ''}`.toLowerCase();
      const email = (candidate.email || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    });
  }, [availableCandidates, candidateSearchTerm]);

  // Close candidate dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      try {
        if (showCandidateDropdown && candidateDropdownRef.current && !candidateDropdownRef.current.contains(event.target)) {
          setShowCandidateDropdown(false);
          setTouched(prev => ({ ...prev, candidateId: true }));
          const error = validateFieldInternal('candidateId', formData.candidateId);
          setErrors(prev => ({ ...prev, candidateId: error }));
        }
      } catch (_) { }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCandidateDropdown]);

  // Helper function to filter input
  const filterAlphanumericSpaceComma = (value, maxLength = Infinity) => {
    const filtered = value.replace(INVALID_CHARS_REGEX, '');
    return maxLength !== Infinity ? filtered.slice(0, maxLength) : filtered;
  };

  // Role-based access control
  const canInitiateVerification = () => {
    return user?.role === 'hr_manager' || user?.role === 'super_admin';
  };

  const handleInputChange = (field, value) => {


    // Sanitize notes field
    let sanitizedValue = value;
    if (field === 'notes' && typeof value === 'string') {
      sanitizedValue = value.replace(INVALID_CHARS_REGEX, '');
      sanitizedValue = sanitizedValue.slice(0, 500);
    }

    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));

    // Perform real-time validation if field has been touched
    if (touched[field]) {
      const error = validateFieldInternal(field, sanitizedValue);
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (errors[field]) {
      // Clear error when user starts typing
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Update selected candidate when candidate changes
    if (field === 'candidateId') {
      const selected = availableCandidates.find(candidate => (candidate.id || candidate._id) === value);
      setSelectedCandidate(selected);
      setSelectedCandidateData(selected);
    }
  };

  // Handle field blur for validation
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    const error = validateFieldInternal(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Special handle blur for Custom components
  const handleCustomBlur = (name, value) => {
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    const error = validateFieldInternal(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };


  // Validation functions
  const validateFieldInternal = (field, value) => {
    switch (field) {
      case 'candidateId':
        if (!value) return 'Please select a candidate';
        return '';
      case 'employeeId':
        if (!value) return 'Please select an employee';
        return '';
      case 'expectedCompletionDate':
        if (!value) return 'Expected completion date is required';
        if (isNaN(new Date(value).getTime())) return 'Invalid date format';
        if (new Date(value) < new Date().setHours(0, 0, 0, 0)) return 'Expected completion date cannot be in the past';
        return '';
      case 'notes':
        if (value && value.trim().length > 500) return 'Notes must be 500 characters or less';
        if (value && !VALID_TEXT_REGEX.test(value)) return 'Notes contains invalid characters';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = ['candidateId', 'employeeId', 'expectedCompletionDate', 'notes'];

    fieldsToValidate.forEach(field => {
      const error = validateFieldInternal(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    // Mark all validated fields as touched
    const allTouched = {};
    fieldsToValidate.forEach(field => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    return Object.keys(newErrors).length === 0;
  };


  // Create or Update background verification mutation using React Query
  const verificationMutation = useMutation({
    mutationFn: async (payload) => {


      const result = isEditMode
        ? await backgroundVerificationService.updateBackgroundVerification(id, payload)
        : await backgroundVerificationService.createBackgroundVerification(payload);

      // Enhanced success detection to handle various API response formats
      const isSuccess = (result && result.responseCode === 201) ||
        (result && result.responseCode === 200) ||
        (result && result.success === true) ||
        (result && result.status === 'success') ||
        (result && (result.id || (result.data && result.data.id)));

      if (!isSuccess) {
        const errorMessage = result?.responseMessage || result?.error || `Failed to ${isEditMode ? 'update' : 'initiate'} background verification`;
        throw new Error(errorMessage);
      }

      return result;
    },
    onSuccess: async (result) => {


      // Send notification email to employee if new or if employee changed
      try {
        const currentEmployeeId = formData.employeeId;
        const employeeChanged = currentEmployeeId !== initialEmployeeId;



        if (!isEditMode || employeeChanged) {

          const employeeEmail = selectedEmployeeData?.contactInfo?.personalEmail ||
            selectedEmployeeData?.contactInfo?.email ||
            selectedEmployeeData?.email ||
            selectedEmployeeData?.user?.email || '';

          const empFirstName = selectedEmployeeData?.personalInfo?.firstName || selectedEmployeeData?.firstName || '';
          const empLastName = selectedEmployeeData?.personalInfo?.lastName || selectedEmployeeData?.lastName || '';
          const employeeName = `${empFirstName} ${empLastName}`.trim() || selectedEmployeeData?.name || 'Employee';

          const candFirstName = selectedCandidateData?.firstName || selectedCandidateData?.personalInfo?.firstName || '';
          const candLastName = selectedCandidateData?.lastName || selectedCandidateData?.personalInfo?.lastName || '';
          const candidateName = `${candFirstName} ${candLastName}`.trim() || selectedCandidateData?.name || 'Candidate';

          const emailResult = await backgroundVerificationService.sendVendorNotification(
            result.data?.id || result.id || id,
            employeeEmail,
            employeeName,
            candidateName,
            'address',
            formData.expectedCompletionDate,
            formData.priority,
            isEditMode ? verificationResult?.data?.status || 'initiated' : 'initiated'
          );

          if (emailResult && emailResult.success) {

          }
        } else {

        }
      } catch (emailError) {

      }

      showSuccess(`Background verification ${isEditMode ? 'updated' : 'initiated'} successfully`);
      navigate('/background_verification');
    },
    onError: (error) => {

      const errorMessage = error.response?.data?.responseMessage || error.message || `Error ${isEditMode ? 'updating' : 'initiating'} background verification`;
      showError(errorMessage);
    },
  });

  const handleSubmit = async (isDraft = false) => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Check if all required fields are filled
    const requiredFields = ['candidateId', 'employeeId', 'expectedCompletionDate'];
    const missingFields = requiredFields.filter(field => {
      return !formData[field] || formData[field].toString().trim() === '';
    });

    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Prepare payload with employee data
    const firstName = selectedEmployeeData?.personalInfo?.firstName || selectedEmployeeData?.firstName || '';
    const lastName = selectedEmployeeData?.personalInfo?.lastName || selectedEmployeeData?.lastName || '';
    const employeeName = `${firstName} ${lastName}`.trim() || selectedEmployeeData?.name || 'Unknown Employee';

    const employeeEmail = selectedEmployeeData?.contactInfo?.personalEmail ||
      selectedEmployeeData?.contactInfo?.email ||
      selectedEmployeeData?.email ||
      selectedEmployeeData?.user?.email || '';

    const employeePhone = selectedEmployeeData?.contactInfo?.phone ||
      selectedEmployeeData?.contactInfo?.mobile ||
      selectedEmployeeData?.phone ||
      selectedEmployeeData?.contactInfo?.personalPhone || '';

    const payload = {
      candidateId: formData.candidateId,
      priority: formData.priority,
      expectedCompletionDate: formData.expectedCompletionDate,
      notes: formData.notes || '',
      vendor: {
        vendorId: formData.employeeId // This is the ID of the selected HR executive/employee
      }
    };

    verificationMutation.mutate(payload);
  };

  if (!canInitiateVerification()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to initiate background verifications.</p>
        </div>
      </div>
    );
  }

  if (loading || (isEditMode && loadingVerification)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600">{isEditMode && loadingVerification ? 'Loading verification data...' : 'Loading candidates...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4 min-w-0 flex-1">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900 break-words">
              {isEditMode ? 'Edit Background Verification' : 'Initiate Background Verification'}
            </h1>
            <p className="text-gray-600 mt-1 break-words">
              {isEditMode
                ? 'Edit existing background verification process for a candidate'
                : 'Start a new background verification process for a candidate'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <form className="space-y-6">
            {/* Candidate Selection */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Candidate Selection</h3>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Candidate <span className="text-red-500">*</span></label>
                  <div className="relative candidate-dropdown w-full max-w-xl" ref={candidateDropdownRef}>
                    {/* Display selected candidate or placeholder */}
                    <div
                      onClick={() => {
                        if (isEditMode) return; // Disable changing candidate in edit mode
                        setShowCandidateDropdown(!showCandidateDropdown);
                        if (!showCandidateDropdown) {
                          setCandidateSearchTerm("");
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-brand focus-within:ring-0 ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} ${errors.candidateId && touched.candidateId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                    >
                      <span className={`min-w-0 flex-1 break-words pr-2 capitalize ${formData.candidateId && selectedCandidateData ? "text-gray-900" : "text-gray-500"}`}>
                        {formData.candidateId && selectedCandidateData
                          ? `${selectedCandidateData.firstName || ""} ${selectedCandidateData.lastName || ""}`
                          : formData.candidateId
                            ? `Candidate ID: ${formData.candidateId}`
                            : "Search candidate..."}
                      </span>
                      {!isEditMode && (
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${showCandidateDropdown ? "transform rotate-180" : ""
                            }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Dropdown with search */}
                    {showCandidateDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by candidate name or email..."
                              value={candidateSearchTerm}
                              onChange={(e) => {
                                const value = e.target.value;
                                const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                setCandidateSearchTerm(filteredValue);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={150}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
                              autoFocus
                            />
                          </div>
                          <div className="mt-1 text-xs text-gray-500 text-right">
                            {candidateSearchTerm.length}/150 characters
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {loadingCandidates ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              Loading candidates...
                            </div>
                          ) : filteredCandidates.length > 0 ? (
                            filteredCandidates.map((candidate) => {
                              const candidateId = candidate.id || candidate._id;
                              const candidateName = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
                              const candidateEmail = candidate.email || '';

                              return (
                                <div
                                  key={candidateId}
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      candidateId: candidateId,
                                    }));
                                    setSelectedCandidateData(candidate);
                                    setShowCandidateDropdown(false);
                                    setCandidateSearchTerm(candidateName);
                                    setErrors(prev => ({ ...prev, candidateId: '' }));
                                    setTouched(prev => ({ ...prev, candidateId: true }));
                                  }}
                                  className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.candidateId === candidateId
                                    ? "bg-blue-50"
                                    : ""
                                    }`}
                                >
                                  <div className="text-sm font-medium text-gray-900 break-words capitalize">
                                    {candidateName}
                                  </div>
                                  <div className="text-xs text-gray-500 break-words">
                                    {candidateEmail}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {candidateSearchTerm.trim()
                                ? "No candidates found"
                                : "No candidates with verified documents available"}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {showCandidateDropdown && candidateSearchTerm.trim()
                      ? `Found ${filteredCandidates.length} candidate(s)`
                      : showCandidateDropdown && filteredCandidates.length > 0
                        ? `Showing ${filteredCandidates.length} candidate(s)`
                        : showCandidateDropdown
                          ? "Loading candidates..."
                          : ""}
                  </div>
                  {selectedCandidateData && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm">
                        <div className="font-medium text-blue-900 break-words capitalize">
                          {selectedCandidateData.firstName || ""}{" "}
                          {selectedCandidateData.lastName || ""}
                        </div>
                        <div className="text-[#0B2033] break-words break-all">
                          {selectedCandidateData.email || "N/A"}
                        </div>
                      </div>
                    </div>
                  )}
                  {errors.candidateId && touched.candidateId && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.candidateId}
                    </p>
                  )}
                  {!loadingCandidates && !hasCandidates && (
                    <p className="text-sm text-amber-600 mt-1">
                      No candidates with verified documents available. Please verify candidate documents first.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Verification Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employee <span className="text-red-500">*</span></label>
                  <div className="relative employee-dropdown w-full max-w-2xl" ref={employeeDropdownRef}>
                    {/* Display selected employee or placeholder */}
                    <div
                      onClick={() => {
                        setShowEmployeeDropdown(!showEmployeeDropdown);
                        if (!showEmployeeDropdown) {
                          setEmployeeSearchTerm("");
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between hover:border-gray-400 focus-within:border-brand focus-within:ring-0 ${errors.employeeId && touched.employeeId ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                        }`}
                    >
                      <span className={`min-w-0 flex-1 break-words pr-2 capitalize ${formData.employeeId && selectedEmployeeData ? "text-gray-900" : "text-gray-500"}`}>
                        {formData.employeeId && selectedEmployeeData
                          ? `${selectedEmployeeData.personalInfo?.firstName || ""} ${selectedEmployeeData.personalInfo?.lastName || ""}`
                          : formData.employeeId
                            ? `Employee ID: ${formData.employeeId}`
                            : "Search employee..."}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${showEmployeeDropdown ? "transform rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {/* Dropdown with search */}
                    {showEmployeeDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search by employee name..."
                              value={employeeSearchTerm}
                              onChange={(e) => {
                                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                                const value = e.target.value;
                                const filteredValue = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 150);
                                setEmployeeSearchTerm(filteredValue);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              maxLength={150}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
                              autoFocus
                            />
                          </div>
                          <div className="mt-1 text-xs text-gray-500 text-right">
                            {employeeSearchTerm.length}/150 characters
                          </div>
                        </div>

                        {/* Results list */}
                        <div className="max-h-64 overflow-y-auto">
                          {(isLoadingEmployeeSearch && debouncedEmployeeSearch.trim()) || (isLoadingDefaultEmployees && !debouncedEmployeeSearch.trim()) ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedEmployeeSearch.trim() ? 'Searching...' : 'Loading employees...'}
                            </div>
                          ) : dropdownEmployees.length > 0 ? (
                            dropdownEmployees.map((employee) => {
                              const employeeName = employee.name || 'Unknown';
                              const employeeEmail = employee.email || 'No email';

                              return (
                                <div
                                  key={employee._id}
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      employeeId: employee._id,
                                    }));
                                    setSelectedEmployeeData(employee);
                                    setShowEmployeeDropdown(false);
                                    setEmployeeSearchTerm(employeeName);
                                    setErrors(prev => ({ ...prev, employeeId: '' }));
                                    setTouched(prev => ({ ...prev, employeeId: true }));
                                  }}
                                  className={`px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 ${formData.employeeId === employee._id
                                    ? "bg-blue-50"
                                    : ""
                                    }`}
                                >
                                  <div className="text-sm font-medium text-gray-900 break-words capitalize">
                                    {employeeName} ({employee.employeeId || 'No ID'})
                                  </div>
                                  <div className="text-xs text-gray-500 break-words">
                                    {employee.department || 'No Dept'} | {employeeEmail}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              {debouncedEmployeeSearch.trim()
                                ? "No employees found"
                                : "Type to search employees..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {showEmployeeDropdown && debouncedEmployeeSearch.trim()
                      ? `Found ${dropdownEmployees.length} employee(s)`
                      : showEmployeeDropdown && dropdownEmployees.length > 0
                        ? `Showing ${dropdownEmployees.length} employee(s)`
                        : showEmployeeDropdown
                          ? "Loading employees..."
                          : ""}
                  </div>
                  {selectedEmployeeData && (
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm">
                        <div className="font-medium text-blue-900 break-words capitalize">
                          {selectedEmployeeData.name || ""}
                        </div>
                        <div className="text-[#0B2033] break-words break-all">
                          {selectedEmployeeData.email || selectedEmployeeData.contactInfo?.personalEmail || selectedEmployeeData.contactInfo?.email || "N/A"}
                        </div>
                      </div>
                    </div>
                  )}
                  {errors.employeeId && touched.employeeId && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.employeeId}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority <span className="text-red-500">*</span></label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expected Completion Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="expectedCompletionDate"
                    value={formData.expectedCompletionDate}
                    onChange={(e) => handleInputChange('expectedCompletionDate', e.target.value)}
                    onBlur={handleBlur}
                    onClick={(e) => e.target.showPicker()}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${errors.expectedCompletionDate && touched.expectedCompletionDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                  />
                  {errors.expectedCompletionDate && touched.expectedCompletionDate && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.expectedCompletionDate}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                onBlur={handleBlur}
                rows={4}
                maxLength={1500}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent ${errors.notes && touched.notes ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                placeholder="Enter any additional instructions or notes..."
              />
              <div className="flex justify-between items-start mt-1">
                <div className="flex-1">
                  {errors.notes && touched.notes && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.notes}
                    </p>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {(formData.notes || '').length}/1500
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                onClick={() => navigate('/background_verification')}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                className="bg-brand hover:bg-brand-dark text-white"
                icon={verificationMutation.isPending ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="h-4 w-4" />}
                onClick={() => handleSubmit(false)}
                disabled={verificationMutation.isPending || (!isEditMode && (!hasCandidates || loadingCandidates))}
              >
                {verificationMutation.isPending
                  ? (isEditMode ? 'Updating...' : 'Initiating...')
                  : (isEditMode ? 'Update Verification' : 'Initiate Verification')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default InitiateBackgroundVerification;
